import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import {
  Area,
  AreaChart,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useDateFilter } from '../contexts/DateFilterContext';
import {
  compareFinancialValues,
  formatFinancialComparison,
  formatFinancialCurrency,
  formatPercentageOfRevenue,
  type FinancialTrend,
} from '../lib/financialComparison';

interface FigureHistoryPoint {
  label: string;
  value: number;
}

interface IncomeStatement {
  revenue?: number;
  cost?: number;
  gross_profit?: number;
  operating_expenses?: number;
  expenses?: number;
  net_income?: number;
}

interface BalanceSheetAccount {
  code?: string;
  name?: string;
  balance?: number;
}

interface BalanceSheet {
  assets?: BalanceSheetAccount[];
  total_assets?: number;
  total_liabilities?: number;
  total_equity?: number;
  as_of?: string;
}

type FavorableDirection = 'favorable' | 'unfavorable' | 'neutral';

const isKnownAmount = (value: unknown): value is number => (
  typeof value === 'number' && Number.isFinite(value)
);

const toneForComparison = (direction: FinancialTrend, favorable: FavorableDirection) => {
  if (direction === 'unavailable' || direction === 'flat') return 'text-muted-foreground';
  const isUp = direction === 'up';
  const positive = favorable === 'favorable' ? isUp : !isUp;
  return positive
    ? 'text-emerald-700 dark:text-emerald-300'
    : 'text-red-700 dark:text-red-300';
};

function MetricBlock({
  label,
  amount,
  amountUnavailable = false,
  amountTone = '',
  comparison,
  comparisonPending,
  comparisonTone,
  accent,
  share,
  history,
  to,
}: {
  label: string;
  amount: number;
  amountUnavailable?: boolean;
  amountTone?: string;
  comparison: string | null;
  comparisonPending: boolean;
  comparisonTone: string;
  accent: string;
  share?: string | null;
  history: FigureHistoryPoint[];
  to: string;
}) {
  const { t } = useTranslation();

  return (
    <Link
      to={to}
      className="group flex h-full flex-col rounded-xl border bg-card p-3 transition-all hover:border-primary/30 hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      style={{ borderTop: `3px solid ${accent}` }}
    >
      <span className="flex items-start justify-between gap-3">
        <span className="min-w-0 text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {label}
        </span>
        {share && (
          <span className="shrink-0 text-xs font-medium text-muted-foreground">
            {share}
          </span>
        )}
      </span>

      <span className="mt-2 flex flex-wrap items-end justify-between gap-x-3 gap-y-2">
        <span
          className={`font-mono text-xl font-bold tabular-nums ${
            amountUnavailable
              ? 'text-muted-foreground'
              : amountTone || (amount < 0 ? 'text-red-900 dark:text-red-200' : 'text-foreground')
          }`}
        >
          {amountUnavailable ? t('dashboard.financialFlash.notAvailable') : formatFinancialCurrency(amount)}
        </span>
        <span
          title={t('dashboard.financialFlash.lastYearComparison')}
          className={`max-w-full text-right font-mono text-xl font-bold leading-7 tabular-nums ${comparisonTone}`}
        >
          {comparisonPending ? '—' : comparison ?? '—'}
        </span>
      </span>

      <span className="mt-3 block h-14">
        {history.length >= 2 ? (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={history} margin={{ top: 2, right: 2, bottom: 2, left: 2 }}>
              <Tooltip
                formatter={(value: any) => formatFinancialCurrency(Number(value || 0))}
                labelFormatter={(label: any) => String(label || '')}
              />
              <Area
                type="monotone"
                dataKey="value"
                name={label}
                stroke={accent}
                strokeWidth={2}
                fill={accent}
                fillOpacity={0.1}
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <span className="flex h-full items-center justify-center rounded-lg border border-dashed px-2 text-center text-[11px] text-muted-foreground">
            {t('dashboard.financialFlash.trendUnavailable')}
          </span>
        )}
      </span>
    </Link>
  );
}

export default function FinancialFlash() {
  const { t } = useTranslation();
  const { user, activeClient } = useAuth();
  const { selectedFY, startDate, endDate, fyOptions } = useDateFilter();
  const queryClient = useQueryClient();

  const selectedIndex = fyOptions.findIndex(option => option.value === selectedFY);
  const previousIndex = selectedIndex > 0 ? selectedIndex - 1 : -1;
  const previousOption = previousIndex >= 0 ? fyOptions[previousIndex] : null;

  // One posted statement per available fiscal year gives every figure its own
  // historical sparkline while continuing to use the existing report APIs.
  const incomeQueries = useQueries({
    queries: fyOptions.map(option => ({
      queryKey: ['income-statement', option.startDate, option.endDate, 'posted'],
      queryFn: (): Promise<IncomeStatement> => api(
        `/bookkeeping/income-statement?start_date=${option.startDate}&end_date=${option.endDate}`,
      ),
      enabled: !!option.startDate && !!option.endDate,
    })),
  });

  const balanceQueries = useQueries({
    queries: fyOptions.map(option => ({
      queryKey: ['balance-sheet', option.endDate, 'posted'],
      queryFn: (): Promise<BalanceSheet> => api(
        `/bookkeeping/balance-sheet?as_of=${option.endDate}`,
      ),
      enabled: !!option.endDate,
    })),
  });

  const currentIncome = selectedIndex >= 0 ? incomeQueries[selectedIndex] : undefined;
  const previousIncome = previousIndex >= 0 ? incomeQueries[previousIndex] : undefined;
  const currentBalance = selectedIndex >= 0 ? balanceQueries[selectedIndex] : undefined;
  const previousBalance = previousIndex >= 0 ? balanceQueries[previousIndex] : undefined;

  const currentData = currentIncome?.data;
  const previousIncomeData = previousIncome?.data;
  const balanceData = currentBalance?.data;
  const previousBalanceData = previousBalance?.data;

  const companyName = activeClient?.display_name
    || activeClient?.company_name
    || user?.company_name
    || t('dashboard.financialFlash.activeCompanyFallback');

  const incomeHistory = (getValue: (data?: IncomeStatement) => unknown): FigureHistoryPoint[] => (
    fyOptions.map((option, index) => ({
      label: `FY ${option.value}`,
      value: getValue(incomeQueries[index]?.data) as number,
    })).filter(point => isKnownAmount(point.value))
  );

  const balanceHistory = (getValue: (data?: BalanceSheet) => unknown): FigureHistoryPoint[] => (
    fyOptions.map((option, index) => ({
      label: `FY ${option.value}`,
      value: getValue(balanceQueries[index]?.data) as number,
    })).filter(point => isKnownAmount(point.value))
  );

  const cashAccount = (balanceData?.assets || []).find(account => (
    /cash|bank/i.test(account.name || '') || String(account.code || '').startsWith('110')
  ));

  const cashHistory = fyOptions.map((option, index) => {
    const data = balanceQueries[index]?.data;
    const account = (data?.assets || []).find(item => (
      /cash|bank/i.test(item.name || '') || String(item.code || '').startsWith('110')
    ));
    return {
      label: `FY ${option.value}`,
      value: account && isKnownAmount(account.balance) ? account.balance : Number.NaN,
    };
  }).filter(point => isKnownAmount(point.value));

  const revenue = Number(currentData?.revenue || 0);
  const revenueForShare = revenue !== 0 ? revenue : null;
  const shareOfRevenue = (value: unknown) => {
    const percentage = formatPercentageOfRevenue(value, revenueForShare);
    if (!percentage) return null;
    return t('dashboard.financialFlash.percentOfSales', { percent: percentage });
  };

  const pAndLRows = [
    {
      key: 'revenue',
      label: t('dashboard.financialFlash.revenue'),
      amount: revenue,
      previous: previousIncomeData?.revenue,
      favorable: 'favorable' as FavorableDirection,
      comparisonPending: !!previousIncome?.isLoading,
      accent: '#22c55e',
      amountTone: '',
      share: null,
      history: incomeHistory(data => data?.revenue),
      to: '/income-statement?pl_focus=revenue',
    },
    {
      key: 'cost',
      label: t('dashboard.financialFlash.directCost'),
      amount: Number(currentData?.cost || 0),
      previous: previousIncomeData?.cost,
      favorable: 'unfavorable' as FavorableDirection,
      comparisonPending: !!previousIncome?.isLoading,
      accent: '#ef4444',
      amountTone: '',
      share: shareOfRevenue(currentData?.cost),
      history: incomeHistory(data => data?.cost),
      to: '/income-statement',
    },
    {
      key: 'grossProfit',
      label: t('dashboard.financialFlash.grossProfit'),
      amount: Number(currentData?.gross_profit || 0),
      previous: previousIncomeData?.gross_profit,
      favorable: 'favorable' as FavorableDirection,
      comparisonPending: !!previousIncome?.isLoading,
      accent: '#3b82f6',
      amountTone: '',
      share: shareOfRevenue(currentData?.gross_profit),
      history: incomeHistory(data => data?.gross_profit),
      to: '/income-statement',
    },
    {
      key: 'operatingExpenses',
      label: t('dashboard.financialFlash.operatingExpenses'),
      amount: Number(currentData?.operating_expenses ?? currentData?.expenses ?? 0),
      previous: previousIncomeData?.operating_expenses ?? previousIncomeData?.expenses,
      favorable: 'unfavorable' as FavorableDirection,
      comparisonPending: !!previousIncome?.isLoading,
      accent: '#f59e0b',
      amountTone: '',
      share: shareOfRevenue(currentData?.operating_expenses ?? currentData?.expenses),
      history: incomeHistory(data => data?.operating_expenses ?? data?.expenses),
      to: '/income-statement?pl_focus=expense',
    },
    {
      key: 'netProfit',
      label: t('dashboard.financialFlash.netProfitLoss'),
      amount: Number(currentData?.net_income || 0),
      previous: previousIncomeData?.net_income,
      favorable: 'favorable' as FavorableDirection,
      comparisonPending: !!previousIncome?.isLoading,
      accent: Number(currentData?.net_income || 0) < 0 ? '#ef4444' : '#10b981',
      amountTone: '',
      share: null,
      history: incomeHistory(data => data?.net_income),
      to: '/income-statement?pl_focus=net',
    },
  ];

  const balanceRows = [
    {
      key: 'totalAssets',
      label: t('dashboard.financialFlash.totalAssets'),
      value: balanceData?.total_assets,
      previous: previousBalanceData?.total_assets,
      accent: '#3b82f6',
      history: balanceHistory(data => data?.total_assets),
    },
    {
      key: 'totalLiabilities',
      label: t('dashboard.financialFlash.totalLiabilities'),
      value: balanceData?.total_liabilities,
      previous: previousBalanceData?.total_liabilities,
      accent: '#f59e0b',
      history: balanceHistory(data => data?.total_liabilities),
    },
    {
      key: 'totalEquity',
      label: t('dashboard.financialFlash.totalEquity'),
      value: balanceData?.total_equity,
      previous: previousBalanceData?.total_equity,
      accent: '#10b981',
      history: balanceHistory(data => data?.total_equity),
    },
    {
      key: 'cash',
      label: t('dashboard.financialFlash.cash'),
      value: cashAccount?.balance,
      previous: previousBalanceData?.assets?.find(account => (
        /cash|bank/i.test(account.name || '') || String(account.code || '').startsWith('110')
      ))?.balance,
      accent: '#06b6d4',
      history: cashHistory,
    },
  ];

  const refreshSnapshot = () => {
    queryClient.invalidateQueries({ queryKey: ['income-statement'] });
    queryClient.invalidateQueries({ queryKey: ['balance-sheet'] });
  };

  return (
    <section className="rounded-2xl border bg-card p-4 shadow-sm" aria-labelledby="financial-flash-heading">
      <div>
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {t('dashboard.financialFlash.company')}
        </p>
        <h2 id="financial-flash-heading" className="mt-1 truncate text-xl font-bold text-foreground">
          {companyName}
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          {selectedFY ? `FY ${selectedFY}` : t('dashboard.financialFlash.noFinancialYear')}
          {startDate && endDate && ` • ${startDate} – ${endDate}`}
        </p>
      </div>

      <div className="mt-4">
        <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {t('dashboard.profitLoss')}
        </h3>
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {currentIncome?.isLoading ? (
            <div className="rounded-xl border p-4 text-sm text-muted-foreground sm:col-span-2 xl:col-span-3">
              {t('dashboard.financialFlash.loading')}
            </div>
          ) : currentIncome?.isError ? (
            <div className="rounded-xl border p-4 sm:col-span-2 xl:col-span-3">
              <p className="text-sm font-medium text-red-900 dark:text-red-200">
                {t('dashboard.financialFlash.unavailable')}
              </p>
              <button
                type="button"
                onClick={refreshSnapshot}
                className="mt-2 rounded-md border bg-background px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted"
              >
                {t('dashboard.financialFlash.retry')}
              </button>
            </div>
          ) : pAndLRows.map(row => {
            const comparison = compareFinancialValues(row.amount, row.previous);
            return (
              <MetricBlock
                key={row.key}
                label={row.label}
                amount={row.amount}
                comparison={formatFinancialComparison(row.amount, row.previous)}
                comparisonPending={row.comparisonPending}
                comparisonTone={toneForComparison(comparison.direction, row.favorable)}
                accent={row.accent}
                amountTone={row.amountTone}
                share={row.share}
                history={row.history}
                to={row.to}
              />
            );
          })}
        </div>
      </div>

      <div className="mt-4">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {t('dashboard.financialFlash.balanceSheet')}
          </h3>
          {balanceData?.as_of && (
            <span className="text-xs text-muted-foreground">
              {t('dashboard.financialFlash.asOf')} {balanceData.as_of}
            </span>
          )}
        </div>
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {currentBalance?.isLoading ? (
            <div className="rounded-xl border p-4 text-sm text-muted-foreground sm:col-span-2 xl:col-span-4">
              {t('dashboard.financialFlash.loading')}
            </div>
          ) : currentBalance?.isError ? (
            <div className="rounded-xl border p-4 sm:col-span-2 xl:col-span-4">
              <p className="text-sm font-medium text-red-900 dark:text-red-200">
                {t('dashboard.financialFlash.unavailable')}
              </p>
              <button
                type="button"
                onClick={refreshSnapshot}
                className="mt-2 rounded-md border bg-background px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted"
              >
                {t('dashboard.financialFlash.retry')}
              </button>
            </div>
          ) : balanceRows.map(row => {
            const amount = isKnownAmount(row.value) ? row.value : 0;
            const currentAmountKnown = isKnownAmount(row.value);
            const comparison = currentAmountKnown
              ? compareFinancialValues(amount, row.previous)
              : undefined;
            return (
              <MetricBlock
                key={row.key}
                label={row.label}
                amount={amount}
                amountUnavailable={!currentAmountKnown}
                amountTone="text-blue-800 dark:text-blue-200"
                comparison={currentAmountKnown ? formatFinancialComparison(amount, row.previous) : null}
                comparisonPending={!!previousBalance?.isLoading}
                comparisonTone={toneForComparison(comparison?.direction ?? 'unavailable', 'neutral')}
                accent={row.accent}
                history={row.history}
                to="/balance-sheet"
              />
            );
          })}
        </div>
      </div>
    </section>
  );
}
